import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Loader2, Zap, Plus, X, Image, Pencil, Search, RefreshCw } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type Listing = {
  id: string;
  title: string;
  teaser: string | null;
  description: string | null;
  price: number;
  revenue: number | null;
  ebitda: number | null;
  sector: string;
  status: string;
  location_state: string | null;
  location_suburb: string | null;
  listing_number: string | null;
  image_url: string | null;
  images: string[] | null;
  featured: boolean | null;
  requires_nda: boolean;
  custom_image: boolean;
};

const SECTORS = [
  "Hospitality",
  "Retail",
  "Services",
  "Health & Beauty",
  "Automotive",
  "Professional Services",
  "Food & Beverage",
  "Education & Training",
  "Transport & Logistics",
  "Manufacturing",
  "Technology",
  "Other"
];

const STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "NT", "ACT"];

export function AdminListingEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [listings, setListings] = useState<Listing[]>([]);
  const [selectedListing, setSelectedListing] = useState<Listing | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Load listings on mount
  useEffect(() => {
    loadAllListings();
  }, []);

  const loadAllListings = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("listings")
        .select("id, title, teaser, description, price, revenue, ebitda, sector, status, location_state, location_suburb, listing_number, image_url, images, featured, requires_nda, custom_image")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setListings(data || []);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (listing: Listing) => {
    setSelectedListing({ ...listing });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!selectedListing) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("listings")
        .update({
          title: selectedListing.title,
          teaser: selectedListing.teaser,
          description: selectedListing.description,
          price: selectedListing.price,
          revenue: selectedListing.revenue,
          ebitda: selectedListing.ebitda,
          sector: selectedListing.sector,
          status: selectedListing.status,
          location_state: selectedListing.location_state,
          location_suburb: selectedListing.location_suburb,
          image_url: selectedListing.image_url,
          images: selectedListing.images,
          featured: selectedListing.featured,
          requires_nda: selectedListing.requires_nda,
          custom_image: (selectedListing.images && selectedListing.images.length > 0) || !!selectedListing.image_url,
        })
        .eq("id", selectedListing.id);

      if (error) throw error;

      toast.success("Listing updated successfully");
      setListings(prev => prev.map(l => l.id === selectedListing.id ? selectedListing : l));
      setDialogOpen(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    if (!selectedListing) return;
    const currentImages = selectedListing.images || [];
    setSelectedListing({ ...selectedListing, images: [...currentImages, ""] });
  };

  const updateImage = (index: number, url: string) => {
    if (!selectedListing) return;
    const newImages = [...(selectedListing.images || [])];
    newImages[index] = url;
    setSelectedListing({ ...selectedListing, images: newImages });
  };

  const removeImage = (index: number) => {
    if (!selectedListing) return;
    const newImages = (selectedListing.images || []).filter((_, i) => i !== index);
    setSelectedListing({ ...selectedListing, images: newImages });
  };

  const updateField = (field: keyof Listing, value: any) => {
    if (!selectedListing) return;
    setSelectedListing({ ...selectedListing, [field]: value });
  };

  const handleMakeLive = async () => {
    if (!selectedListing) return;

    setActivating(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-activate-listing", {
        body: { listing_id: selectedListing.id }
      });

      if (error) throw error;

      toast.success(`Listing activated! Number: ${data.listing_number}`);
      const updatedListing = { ...selectedListing, status: "live", listing_number: data.listing_number };
      setSelectedListing(updatedListing);
      setListings(prev => prev.map(l => l.id === selectedListing.id ? updatedListing : l));
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setActivating(false);
    }
  };

  const filteredListings = listings.filter(l =>
    l.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    l.listing_number?.includes(searchTerm) ||
    l.sector.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      live: "default",
      draft: "secondary",
      sold: "outline",
      withdrawn: "destructive"
    };
    return <Badge variant={variants[status] || "secondary"}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>All Listings ({listings.length})</span>
          <Button variant="outline" size="sm" onClick={loadAllListings} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, number, or sector..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Listings Table */}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">#</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Sector</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-20">Image</TableHead>
                  <TableHead className="w-20">Edit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredListings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No listings found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredListings.map((listing) => (
                    <TableRow key={listing.id}>
                      <TableCell className="font-mono text-sm">
                        {listing.listing_number || "—"}
                      </TableCell>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {listing.title}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {listing.sector}
                      </TableCell>
                      <TableCell className="font-medium">
                        ${listing.price.toLocaleString()}
                      </TableCell>
                      <TableCell>{getStatusBadge(listing.status)}</TableCell>
                      <TableCell>
                        {(listing.images?.length || listing.image_url) ? (
                          <Image className="h-4 w-4 text-green-600" />
                        ) : (
                          <span className="text-muted-foreground text-xs">None</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(listing)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Listing #{selectedListing?.listing_number || "Draft"}</DialogTitle>
            </DialogHeader>
            
            {selectedListing && (
              <div className="space-y-4 pt-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={selectedListing.title}
                    onChange={(e) => updateField("title", e.target.value)}
                  />
                </div>

                <div>
                  <Label>Teaser</Label>
                  <Textarea
                    value={selectedListing.teaser || ""}
                    onChange={(e) => updateField("teaser", e.target.value)}
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={selectedListing.description || ""}
                    onChange={(e) => updateField("description", e.target.value)}
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Price ($)</Label>
                    <Input
                      type="number"
                      value={selectedListing.price}
                      onChange={(e) => updateField("price", parseFloat(e.target.value) || 0)}
                    />
                  </div>
                  <div>
                    <Label>Revenue ($)</Label>
                    <Input
                      type="number"
                      value={selectedListing.revenue || ""}
                      onChange={(e) => updateField("revenue", e.target.value ? parseFloat(e.target.value) : null)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>EBITDA ($)</Label>
                    <Input
                      type="number"
                      value={selectedListing.ebitda || ""}
                      onChange={(e) => updateField("ebitda", e.target.value ? parseFloat(e.target.value) : null)}
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={selectedListing.status} onValueChange={(v) => updateField("status", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="live">Live</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                        <SelectItem value="withdrawn">Withdrawn</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sector</Label>
                    <Select value={selectedListing.sector} onValueChange={(v) => updateField("sector", v)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTORS.map((sector) => (
                          <SelectItem key={sector} value={sector}>{sector}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>State</Label>
                    <Select value={selectedListing.location_state || ""} onValueChange={(v) => updateField("location_state", v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {STATES.map((state) => (
                          <SelectItem key={state} value={state}>{state}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Suburb</Label>
                  <Input
                    value={selectedListing.location_suburb || ""}
                    onChange={(e) => updateField("location_suburb", e.target.value)}
                  />
                </div>

                {/* Images Section */}
                <div className="space-y-3 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Image className="h-4 w-4" />
                      Images ({(selectedListing.images || []).length})
                    </Label>
                    <Button type="button" variant="outline" size="sm" onClick={addImage}>
                      <Plus className="h-4 w-4 mr-1" /> Add Image
                    </Button>
                  </div>
                  
                  {(selectedListing.images || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No images. Use local paths like /images/categories/cafe-interior.jpg</p>
                  )}
                  
                  <div className="space-y-2">
                    {(selectedListing.images || []).map((img, index) => (
                      <div key={index} className="flex gap-2 items-start">
                        <div className="flex-1 space-y-1">
                          <Input
                            placeholder="/images/categories/restaurant.jpg"
                            value={img}
                            onChange={(e) => updateImage(index, e.target.value)}
                          />
                          {img && (
                            <img 
                              src={img} 
                              alt={`Preview ${index + 1}`} 
                              className="h-16 w-24 object-cover rounded border"
                              onError={(e) => (e.currentTarget.style.display = 'none')}
                            />
                          )}
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          onClick={() => removeImage(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Primary Image URL (legacy)</Label>
                  <Input
                    value={selectedListing.image_url || ""}
                    onChange={(e) => updateField("image_url", e.target.value)}
                    placeholder="Optional - use images array above instead"
                  />
                </div>

                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={selectedListing.featured || false}
                      onCheckedChange={(v) => updateField("featured", v)}
                    />
                    <Label>Featured</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={selectedListing.requires_nda}
                      onCheckedChange={(v) => updateField("requires_nda", v)}
                    />
                    <Label>Requires NDA</Label>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Button onClick={handleSave} disabled={saving} className="flex-1">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                  {selectedListing.status === "draft" && (
                    <Button onClick={handleMakeLive} disabled={activating} variant="default" className="bg-green-600 hover:bg-green-700">
                      {activating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Zap className="h-4 w-4 mr-2" />}
                      Make Live
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
